import dayjs from 'dayjs';
import { request } from '@/data/request';
import { parseDateAsUTC } from '@/utils/formatUtils';
import { formatRoomLabel } from '@/views/myEstablishment/establishmentRoom/services/myEstablishmentRoomService';

export interface TeleattendanceRoomWorkday {
  day_of_week: number;
  opening_time: string;
  closing_time: string;
}

export interface TeleattendanceItem {
  schedulePlanningId: number;
  schedulePlanningProfessionalId: number;
  date: string;
  startTime: string;
  endTime: string;
  establishmentName: string;
  roomId: number;
  roomName: string;
  roomType: string;
  roomWorkdays: TeleattendanceRoomWorkday[];
  departmentName?: string;
  specialtyName?: string;
  statusName?: string;
  statusId?: number;
  professionalName?: string;
}

export type TeleattendanceSessionStatus =
  | 'link_generated'
  | 'waiting_patient'
  | 'in_progress'
  | 'completed';

export type TeleattendanceQueueStatus =
  | 'pending'
  | 'waiting'
  | 'in_progress'
  | 'completed'
  | 'cancelled';

export type TeleattendanceTriageStatus =
  | 'not_started'
  | 'in_progress'
  | 'completed';

export type TeleattendanceUrgencyLevel = 'low' | 'medium' | 'high' | 'urgent';

export interface TeleattendanceTriageTurn {
  question: string;
  answer: string;
}

export interface TeleattendanceTriage {
  status: TeleattendanceTriageStatus;
  currentQuestion?: string;
  summary?: string;
  keyInsights: string[];
  urgencyLevel?: TeleattendanceUrgencyLevel | null;
  urgencyReason?: string;
  transcript: TeleattendanceTriageTurn[];
  confidence?: number | null;
  startedAt?: string | null;
  completedAt?: string | null;
  termsAcceptedAt?: string | null;
}

export interface TeleattendanceQueueEntry {
  id: number;
  patientName: string;
  patientEmail: string;
  patientAge?: number | null;
  patientGender?: string | null;
  accessToken: string;
  publicPath: string;
  publicLink: string;
  queueStatus: TeleattendanceQueueStatus;
  joinedAt: string;
  claimedAt?: string | null;
  linkStartsAt?: string | null;
  linkExpiresAt?: string | null;
  startedAt?: string | null;
  endedAt?: string | null;
  position?: number | null;
  triage?: TeleattendanceTriage | null;
}

export interface TeleattendanceSession {
  schedulePlanningProfessionalId: number;
  publicToken: string;
  publicPath: string;
  publicLink: string;
  sessionStatus: TeleattendanceSessionStatus;
  queueEntries: TeleattendanceQueueEntry[];
  activeQueueEntry?: TeleattendanceQueueEntry | null;
  latestCompletedEntry?: TeleattendanceQueueEntry | null;
}

export interface PublicTeleattendanceSession {
  schedulePlanningProfessionalId: number;
  establishmentName: string;
  roomName: string;
  roomEnableTriage: boolean;
  specialtyName?: string;
  departmentName?: string;
  date: string;
  startTime?: string | null;
  endTime?: string | null;
  sessionStatus: TeleattendanceSessionStatus;
  queueLength: number;
  queueEntry?: TeleattendanceQueueEntry | null;
}

export interface TeleattendanceVideoConnection {
  provider: string;
  serverUrl: string;
  roomName: string;
  participantIdentity: string;
  participantName: string;
  participantMetadata?: string;
  scheduledStartAt?: string;
  scheduledEndAt?: string;
  startedAt?: string;
  token: string;
  chatAccessToken?: string;
  chatAccessTokenExpiresAt?: string;
}

const buildAbsolutePublicLink = (publicPath: string) => {
  if (!publicPath) return '';
  if (typeof window === 'undefined') return publicPath;
  return `${window.location.origin}${publicPath}`;
};

const normalizeShift = (item: any): TeleattendanceItem[] => {
  const professionals = Array.isArray(item?.professionals)
    ? item.professionals
    : [];

  return professionals
    .filter(
      (professional: any) =>
        professional?.establishment_config_room?.room_type === 'virtual',
    )
    .map((professional: any) => {
      const room = professional?.establishment_config_room;

      return {
        schedulePlanningId: Number(item?.ID || item?.id || 0),
        schedulePlanningProfessionalId: Number(
          professional?.id || item?.schedule_planning_professional_id || 0,
        ),
        date: String(item?.date || ''),
        startTime: String(professional?.start_time || ''),
        endTime: String(professional?.end_time || ''),
        establishmentName:
          item?.establishment_config?.establishment?.trade_name ||
          item?.establishment_config?.establishment?.company_name ||
          item?.establishment_config?.establishment?.name ||
          'Estabelecimento',
        roomId: Number(room?.ID || room?.id || 0),
        roomName: formatRoomLabel(room),
        roomType: String(room?.room_type || 'virtual'),
        roomWorkdays: Array.isArray(room?.room_workdays)
          ? room.room_workdays.map((workday: any) => ({
              day_of_week: Number(workday?.day_of_week ?? 0),
              opening_time: String(workday?.opening_time || '').slice(0, 5),
              closing_time: String(workday?.closing_time || '').slice(0, 5),
            }))
          : [],
        departmentName: professional?.sector?.department_name,
        specialtyName: professional?.speciality?.name,
        statusName: professional?.schedule_planning_status?.name,
        statusId:
          professional?.schedule_planning_status?.ID ||
          professional?.schedule_planning_status?.id,
        professionalName:
          professional?.professional?.user?.name ||
          professional?.professional?.name ||
          undefined,
      } satisfies TeleattendanceItem;
    })
    .filter(
      (entry) => entry.schedulePlanningProfessionalId > 0 && entry.roomId > 0,
    );
};

const normalizeQueueEntry = (data: any): TeleattendanceQueueEntry => ({
  id: Number(data?.id || 0),
  patientName: String(data?.patient_name || ''),
  patientEmail: String(data?.patient_email || ''),
  patientAge:
    data?.patient_age === undefined || data?.patient_age === null
      ? null
      : Number(data.patient_age),
  patientGender: data?.patient_gender || null,
  accessToken: String(data?.access_token || ''),
  publicPath: String(data?.public_path || ''),
  publicLink: buildAbsolutePublicLink(String(data?.public_path || '')),
  queueStatus: String(
    data?.queue_status || 'waiting',
  ) as TeleattendanceQueueStatus,
  joinedAt: String(data?.joined_at || ''),
  claimedAt: data?.claimed_at || null,
  linkStartsAt: data?.link_starts_at || null,
  linkExpiresAt: data?.link_expires_at || null,
  startedAt: data?.started_at || null,
  endedAt: data?.ended_at || null,
  position:
    data?.position === undefined || data?.position === null
      ? null
      : Number(data.position),
  triage: data?.triage
    ? {
        status: String(
          data.triage?.status || 'not_started',
        ) as TeleattendanceTriageStatus,
        currentQuestion: data.triage?.current_question || '',
        summary: data.triage?.summary || '',
        keyInsights: Array.isArray(data.triage?.key_insights)
          ? data.triage.key_insights.map(String)
          : [],
        urgencyLevel:
          (data.triage?.urgency_level as TeleattendanceUrgencyLevel) || 'low',
        urgencyReason: data.triage?.urgency_reason || '',
        transcript: Array.isArray(data.triage?.transcript)
          ? data.triage.transcript.map((turn: any) => ({
              question: String(turn?.question || ''),
              answer: String(turn?.answer || ''),
            }))
          : [],
        confidence:
          data.triage?.confidence === undefined ||
          data.triage?.confidence === null
            ? null
            : Number(data.triage.confidence),
        startedAt: data.triage?.started_at || null,
        completedAt: data.triage?.completed_at || null,
        termsAcceptedAt: data.triage?.terms_accepted_at || null,
      }
    : null,
});

const normalizeProfessionalSession = (response: any): TeleattendanceSession => {
  const data = response?.data || {};
  const publicToken = String(data?.public_token || '');
  const publicPath = String(
    response?.public_path ||
      (publicToken ? `/teleattendance/public/${publicToken}` : ''),
  );

  return {
    schedulePlanningProfessionalId: Number(
      data?.schedule_planning_professional_id || 0,
    ),
    publicToken,
    publicPath,
    publicLink: buildAbsolutePublicLink(publicPath),
    sessionStatus: String(
      data?.session_status || 'link_generated',
    ) as TeleattendanceSessionStatus,
    queueEntries: Array.isArray(data?.queue_entries)
      ? data.queue_entries.map(normalizeQueueEntry)
      : [],
    activeQueueEntry: data?.active_queue_entry
      ? normalizeQueueEntry(data.active_queue_entry)
      : null,
    latestCompletedEntry: data?.latest_completed_entry
      ? normalizeQueueEntry(data.latest_completed_entry)
      : null,
  };
};

const normalizePublicSession = (response: any): PublicTeleattendanceSession => {
  const data = response?.data || {};

  return {
    schedulePlanningProfessionalId: Number(
      data?.schedule_planning_professional_id || 0,
    ),
    establishmentName: String(data?.establishment_name || 'Estabelecimento'),
    roomName: String(data?.room_name || 'Sala virtual'),
    roomEnableTriage: Boolean(data?.room_enable_triage),
    specialtyName: data?.specialty_name || '',
    departmentName: data?.department_name || '',
    date: String(data?.date || ''),
    startTime: data?.start_time || null,
    endTime: data?.end_time || null,
    sessionStatus: String(
      data?.session_status || 'link_generated',
    ) as TeleattendanceSessionStatus,
    queueLength: Number(data?.queue_length || 0),
    queueEntry: data?.queue_entry
      ? normalizeQueueEntry(data.queue_entry)
      : null,
  };
};

const normalizeVideoConnection = (
  response: any,
): TeleattendanceVideoConnection => {
  const data = response?.data || {};

  return {
    provider: String(data?.provider || 'livekit'),
    serverUrl: String(data?.server_url || ''),
    roomName: String(data?.room_name || ''),
    participantIdentity: String(data?.participant_identity || ''),
    participantName: String(data?.participant_name || ''),
    token: String(data?.token || ''),
  };
};

export const fetchTodayTeleattendance = async (): Promise<
  TeleattendanceItem[]
> => {
  const response = await request<any>({
    method: 'get',
    url: '/teleattendance/today-rooms',
  });

  const items = Array.isArray(response?.data) ? response.data : [];

  return items
    .map(normalizeRoomSummary)
    .filter((item): item is TeleattendanceItem => Boolean(item))
    .sort(
      (a, b) =>
        dayjs(parseDateAsUTC(a.startTime) || a.startTime).valueOf() -
        dayjs(parseDateAsUTC(b.startTime) || b.startTime).valueOf(),
    );
};

const normalizeRoomSummary = (item: any): TeleattendanceItem | null => {
  const roomId = Number(item?.room_id || 0);
  const schedulePlanningProfessionalId = Number(
    item?.schedule_planning_professional_id || 0,
  );

  if (roomId <= 0 || schedulePlanningProfessionalId <= 0) {
    return null;
  }

  return {
    schedulePlanningId: Number(item?.schedule_planning_id || 0),
    schedulePlanningProfessionalId,
    date: String(item?.date || ''),
    startTime: String(item?.start_time || ''),
    endTime: String(item?.end_time || ''),
    establishmentName: String(item?.establishment_name || 'Estabelecimento'),
    roomId,
    roomName: String(item?.room_name || 'Sala virtual'),
    roomType: String(item?.room_type || 'virtual'),
    roomWorkdays: [],
    departmentName: item?.department_name || undefined,
    specialtyName: item?.specialty_name || undefined,
    statusName: item?.status_name || undefined,
    statusId:
      item?.status_id === undefined || item?.status_id === null
        ? undefined
        : Number(item.status_id),
    professionalName: item?.professional_name || undefined,
  };
};

export const fetchPreviousTeleattendanceRooms = async (): Promise<
  TeleattendanceItem[]
> => {
  const response = await request<any>({
    method: 'get',
    url: '/teleattendance/recent-rooms',
  });

  const items = Array.isArray(response?.data) ? response.data : [];

  return items
    .map(normalizeRoomSummary)
    .filter((item): item is TeleattendanceItem => Boolean(item))
    .sort((a, b) => dayjs(b.endTime).valueOf() - dayjs(a.endTime).valueOf());
};

export const fetchTeleattendanceSession = async (
  schedulePlanningProfessionalId: number,
): Promise<TeleattendanceSession> => {
  const response = await request<any>({
    method: 'get',
    url: `/teleattendance/session/${schedulePlanningProfessionalId}`,
  });

  return normalizeProfessionalSession(response);
};

export const startTeleattendanceSession = async (
  schedulePlanningProfessionalId: number,
): Promise<TeleattendanceSession> => {
  const response = await request<any>({
    method: 'post',
    url: `/teleattendance/session/${schedulePlanningProfessionalId}/start`,
    data: {},
  });

  return normalizeProfessionalSession(response);
};

export const startTeleattendanceQueueEntry = async (
  schedulePlanningProfessionalId: number,
  queueEntryId: number,
): Promise<TeleattendanceSession> => {
  const response = await request<any>({
    method: 'post',
    url: `/teleattendance/session/${schedulePlanningProfessionalId}/queue/${queueEntryId}/start`,
    data: {},
  });

  return normalizeProfessionalSession(response);
};

export const endTeleattendanceSession = async (
  schedulePlanningProfessionalId: number,
): Promise<TeleattendanceSession> => {
  const response = await request<any>({
    method: 'post',
    url: `/teleattendance/session/${schedulePlanningProfessionalId}/end`,
    data: {},
  });

  return normalizeProfessionalSession(response);
};

export const endTeleattendanceQueueEntry = async (
  schedulePlanningProfessionalId: number,
  queueEntryId: number,
): Promise<TeleattendanceSession> => {
  const response = await request<any>({
    method: 'post',
    url: `/teleattendance/session/${schedulePlanningProfessionalId}/queue/${queueEntryId}/end`,
    data: {},
  });

  return normalizeProfessionalSession(response);
};

export const cancelTeleattendanceQueueEntry = async (
  schedulePlanningProfessionalId: number,
  queueEntryId: number,
): Promise<TeleattendanceSession> => {
  const response = await request<any>({
    method: 'post',
    url: `/teleattendance/session/${schedulePlanningProfessionalId}/queue/${queueEntryId}/cancel`,
    data: {},
  });

  return normalizeProfessionalSession(response);
};

export const fetchPublicTeleattendanceSession = async (
  token: string,
): Promise<PublicTeleattendanceSession> => {
  const response = await request<any>({
    method: 'get',
    url: `/teleattendance-public/session/${token}`,
  });

  return normalizePublicSession(response);
};

export const joinPublicTeleattendanceSession = async (
  token: string,
  patientName: string,
  patientEmail: string,
  patientAge: number,
  patientGender: string,
): Promise<PublicTeleattendanceSession> => {
  const response = await request<any>({
    method: 'post',
    url: `/teleattendance-public/session/${token}/join`,
    data: {
      patient_name: patientName,
      patient_email: patientEmail,
      patient_age: patientAge,
      patient_gender: patientGender,
    },
  });

  return normalizePublicSession(response);
};

export const cancelPublicTeleattendanceQueueEntry = async (
  token: string,
): Promise<PublicTeleattendanceSession> => {
  const response = await request<any>({
    method: 'post',
    url: `/teleattendance-public/session/${token}/cancel`,
    data: {},
  });

  return normalizePublicSession(response);
};

export const startPublicTeleattendanceTriage = async (
  token: string,
  patientName: string,
  patientEmail: string,
  patientAge: number,
  patientGender: string,
  acceptedTerms: boolean,
): Promise<PublicTeleattendanceSession> => {
  const response = await request<any>({
    method: 'post',
    url: `/teleattendance-public/session/${token}/triage/start`,
    data: {
      patient_name: patientName,
      patient_email: patientEmail,
      patient_age: patientAge,
      patient_gender: patientGender,
      accepted_terms: acceptedTerms,
    },
  });

  return normalizePublicSession(response);
};

export const answerPublicTeleattendance = async (
  token: string,
  answer: string,
): Promise<PublicTeleattendanceSession> => {
  const response = await request<any>({
    method: 'post',
    url: `/teleattendance-public/session/${token}/triage/answer`,
    data: {
      answer,
    },
  });

  return normalizePublicSession(response);
};

export const fetchTeleattendanceVideoConnection = async (
  schedulePlanningProfessionalId: number,
): Promise<TeleattendanceVideoConnection> => {
  const response = await request<any>({
    method: 'get',
    url: `/teleattendance/session/${schedulePlanningProfessionalId}/video-connection`,
  });

  return normalizeVideoConnection(response);
};

export const fetchPublicTeleattendanceVideoConnection = async (
  token: string,
): Promise<TeleattendanceVideoConnection> => {
  const response = await request<any>({
    method: 'get',
    url: `/teleattendance-public/session/${token}/video-connection`,
  });

  return normalizeVideoConnection(response);
};

export const createTeleattendanceQueueEntryLink = async (
  schedulePlanningProfessionalId: number,
  patientName: string,
  linkDurationMinutes = 15,
): Promise<TeleattendanceSession> => {
  const response = await request<any>({
    method: 'post',
    url: `/teleattendance/session/${schedulePlanningProfessionalId}/queue-link`,
    data: {
      patient_name: patientName,
      link_duration_minutes: linkDurationMinutes,
    },
  });

  return normalizeProfessionalSession(response);
};
